// Agency "Richiedi un contatto" lead emails — homepage "Sei un'agenzia?" section
// and the agencies page (/agenzie).
//
// Sends two emails through the shared Mailjet "madre" template (id 8097000):
//  1. an internal notification to the immaginacasa commercial inbox(es), and
//  2. a confirmation to the agent who filled the form.
// Pure builders + thin orchestration so they can be unit-tested without Firebase,
// mirroring services/videocall.js.

import {
  VIDEOCALL_EMAIL_TEMPLATE_ID as MADRE_TEMPLATE_ID,
  madreVariables,
  brandConfig,
  escapeHtml,
} from "./videocall";

// Fixed internal recipients — never client-controlled (anti open-relay), like the
// videocall internal recipients. These are the immaginacasa commercial contacts.
export const AGENCY_CONTACT_RECIPIENTS = [
  { email: "i.bragato@immaginacasa.com", name: "Bragato" },
  { email: "info@immaginacasa.com", name: "Immaginacasa" },
];

// Where the form was filled in, as told to the commercial team. Unknown values
// fall back to the homepage (the original, and default, origin).
const AGENCY_CONTACT_ORIGINS = {
  home: `sezione "Sei un'agenzia?" della homepage`,
  agenzie: `pagina "Agenzie" del sito`,
};

// Build the internal-notification payload. The form fields are all HTML-escaped
// before being interpolated into the (HTML) email body.
export function buildAgencyContactInternalEmailPayload({
  firstName,
  lastName,
  email,
  agencyName,
  agencyAddress,
  source = "home",
  brand = "immaginacasa",
} = {}) {
  const b = brandConfig(brand);
  const fullName = `${firstName ?? ""} ${lastName ?? ""}`.trim();
  const title = "Nuova richiesta di contatto agenzia";
  const origin = AGENCY_CONTACT_ORIGINS[source] || AGENCY_CONTACT_ORIGINS.home;
  const body =
    `Nuova richiesta di contatto da un'agenzia (${origin}).<br><br>` +
    `Referente: ${escapeHtml(fullName)}<br>` +
    `Email: ${escapeHtml(email)}<br>` +
    `Agenzia: ${escapeHtml(agencyName)}<br>` +
    `Sede: ${escapeHtml(agencyAddress)}`;

  return {
    templateId: MADRE_TEMPLATE_ID,
    sendingEmail: b.senderEmail,
    sendingName: b.senderName,
    subject: title,
    variables: madreVariables({ title, body, logoUrl: b.logoUrl }),
    recipients: AGENCY_CONTACT_RECIPIENTS,
  };
}

// Build the confirmation sent back to the agent's own address (the one they typed
// in the form). Client-controlled recipient by design — it's a receipt.
export function buildAgencyContactConfirmationEmailPayload({
  firstName,
  lastName,
  email,
  agencyName,
  brand = "immaginacasa",
} = {}) {
  const b = brandConfig(brand);
  const fullName = `${firstName ?? ""} ${lastName ?? ""}`.trim();
  const greeting = firstName ? `Ciao ${escapeHtml(firstName)},` : "Ciao,";
  const title = "Abbiamo ricevuto la tua richiesta";
  const body =
    `${greeting}<br><br>` +
    `abbiamo ricevuto la tua richiesta di contatto per l'agenzia ` +
    `<strong>${escapeHtml(agencyName)}</strong>. Il nostro team commerciale ti ` +
    `ricontatterà il prima possibile.<br><br>` +
    `Grazie,<br>Il team di Immaginacasa`;

  return {
    templateId: MADRE_TEMPLATE_ID,
    sendingEmail: b.senderEmail,
    sendingName: b.senderName,
    subject: title,
    variables: madreVariables({ title, body, logoUrl: b.logoUrl }),
    recipients: [{ email, name: fullName || email }],
  };
}

// Send both emails: the agent's confirmation first, then the internal notification
// (order mirrors the videocall flow). `sendEmail` is injected (the wired-up
// Firebase callable in the component) so this stays testable. Caller decides how
// to treat failures.
export async function sendAgencyContactEmails({ sendEmail, ...form }) {
  await sendEmail(buildAgencyContactConfirmationEmailPayload(form));
  await sendEmail(buildAgencyContactInternalEmailPayload(form));
}
